import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  applyAppDataUpdates,
  PublicSessionData,
  SessionAppData,
  SessionAppDataUpdates,
} from '@pedix-workspace/shared-session-cookie';
import { firstValueFrom } from 'rxjs';
import { isPlatformServer } from '@angular/common';

const EMPTY_PUBLIC_SESSION_DATA: PublicSessionData = {
  app: {},
  user: null,
};

const SESSION_DATA_REQUEST_TIMEOUT = 5000;

/**
 * Estado de `__session` en el cliente.
 *
 * La cookie es `HttpOnly`, así que el estado se hidrata una única vez desde el servidor y a partir
 * de ahí se mantiene local: cada escritura devuelve el estado resultante en la misma respuesta, sin
 * necesidad de re-consultar.
 *
 * Expone dos superficies:
 * - `sessionData`: signal, para consumidores reactivos (computed, templates, effects).
 * - `getSessionData()`: promesa, para consumidores imperativos que necesitan el dato ya resuelto.
 */
@Injectable({ providedIn: 'root' })
export class SessionAppDataService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  private readonly state = signal<PublicSessionData>(EMPTY_PUBLIC_SESSION_DATA);
  private hydration: Promise<void> | null = null;

  readonly sessionData = this.state.asReadonly();

  get authServiceBaseUrl() {
    return ''; // API function is mapped to current domain using firebase rewrite rules
  }

  constructor() {
    // La hidratación arranca acá y no en la primera lectura: todas las páginas terminan leyendo la
    // sesión, y disparar el fetch desde un computed sería un efecto secundario dentro del grafo reactivo
    void this.ensureHydrated();
  }

  /**
   * Devuelve el estado vigente (no el que resolvió el fetch), para que una lectura posterior a una
   * escritura vea el valor actualizado.
   */
  async getSessionData(): Promise<PublicSessionData> {
    await this.ensureHydrated();

    return this.state();
  }

  async setLanguage(language: string): Promise<void> {
    await this.updateAppData({ language });
  }

  async setSelectedSubsidiaryId(
    parentSlug: string,
    subsidiaryId: string,
    isUserSelected: boolean,
  ): Promise<void> {
    await this.updateAppData({
      [`selectedSubsidiaryMap.${parentSlug}`]: { id: subsidiaryId, isUserSelected },
    });
  }

  /**
   * `lastOrderCoordinates` se escribe en la misma llamada para no pagar dos round-trips, y sólo
   * cuando viene definido: mandarlo `undefined` borraría las coordenadas del pedido anterior.
   */
  async setEndOrderData(
    endOrderData: SessionAppData['endOrderData'],
    lastOrderCoordinates?: SessionAppData['lastOrderCoordinates'],
  ): Promise<void> {
    await this.updateAppData(
      lastOrderCoordinates ? { endOrderData, lastOrderCoordinates } : { endOrderData },
    );
  }

  /**
   * Un request fallido NO se memoiza: si se cacheara la respuesta vacía, un blip de red durante el
   * bootstrap dejaría el pre-llenado desactivado durante toda la sesión de página, sin reintento.
   */
  private ensureHydrated(): Promise<void> {
    if (!this.hydration) {
      const request = this.fetchSessionData()
        .then(data => this.state.set(data))
        .catch(error => {
          // Comparar identidad evita que un request viejo invalide el cache de uno más nuevo
          if (this.hydration === request) {
            this.hydration = null;
          }
          // NOTE: console.error y NO NGXLogger a propósito. Inyectar NGXLogger acá cierra un ciclo
          // de DI (NG0200) que rompe la extracción de rutas del SSR:
          // NGXLogger → TOKEN_LOGGER_SERVER_SERVICE → ERROR_HANDLER_CONFIG → AppRequestContextService
          // → SessionAppDataService → NGXLogger. Ver la nota equivalente en app-request-context.service.ts
          console.error('Error fetching session data', error);
        });

      this.hydration = request;
    }

    return this.hydration;
  }

  private async fetchSessionData(): Promise<PublicSessionData> {
    // En SSR la URL relativa no es resoluble por el HttpClient de servidor, y el estado de sesión
    // se resuelve por middleware sobre la cookie del request
    if (isPlatformServer(this.platformId)) {
      return EMPTY_PUBLIC_SESSION_DATA;
    }

    const response = await firstValueFrom(
      this.http.get<PublicSessionData>(`${this.authServiceBaseUrl}/auth/session/app`, {
        withCredentials: true,
        // El pre-llenado del checkout espera este request: sin tope, un request colgado deja la
        // pantalla en loading indefinidamente
        timeout: SESSION_DATA_REQUEST_TIMEOUT,
      }),
    );

    return {
      app: response.app || {},
      user: response.user || null,
    };
  }

  /**
   * El patch se aplica al estado local de forma SÍNCRONA y recién después se sincroniza con el
   * servidor, sin esperar la respuesta para actualizar nada.
   *
   * Así el estado local es simplemente la acumulación de los patches en el orden en que el cliente
   * los aplicó, y dos escrituras concurrentes no pueden pisarse: no hay respuestas que ordenar.
   * El merge usa la misma función que aplica el servidor, así que ambos llegan al mismo resultado.
   */
  private async updateAppData(appData: SessionAppDataUpdates): Promise<unknown> {
    // Esperar la hidratación garantiza que el patch se aplique sobre el estado del servidor y no
    // sobre uno vacío. Ya está en vuelo desde el constructor, así que en la práctica no espera nada
    await this.ensureHydrated();

    this.state.update(current => ({
      ...current,
      app: applyAppDataUpdates(current.app, appData),
    }));

    return firstValueFrom(
      this.http.post(`${this.authServiceBaseUrl}/auth/session/app`, appData, {
        withCredentials: true,
        keepalive: true,
      }),
    );
  }
}
